/**
 * ADJUDON Axios API Client
 *
 * Production-ready API client with:
 * - Request/Response interceptors
 * - Automatic token refresh
 * - Exponential backoff retry (5xx)
 * - Circuit breaker (5 consecutive failures → open)
 * - Error categorization (system / user / network / rate_limit / auth)
 * - Graceful 429 with Retry-After countdown
 * - CSRF token support
 * - Request deduplication
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ENV, isDevelopment } from '../config/env';
import { toast } from 'sonner';
import { addPendingRequest, removePendingRequest } from './requestDeduplication';

// ============================================================================
// ERROR CATEGORIZATION
// ============================================================================

export type ErrorCategory = 'system' | 'user' | 'network' | 'rate_limit' | 'auth';

function categorizeError(status: number | undefined, isNetworkError: boolean): ErrorCategory {
  if (isNetworkError) return 'network';
  if (!status) return 'network';
  if (status === 401 || status === 403) return 'auth';
  if (status === 429) return 'rate_limit';
  if (status >= 400 && status < 500) return 'user';
  return 'system'; // 5xx
}

// ============================================================================
// API ERROR CLASS
// ============================================================================

export class APIError extends Error {
  public readonly category: ErrorCategory;

  constructor(
    public status: number,
    public code: string,
    message: string,
    public data?: any,
    category?: ErrorCategory
  ) {
    super(message);
    this.name = 'APIError';
    this.category = category ?? categorizeError(status, false);
  }
}

// Error event emitter for global error handling
type ErrorHandler = (error: APIError) => void;
let errorHandlers: ErrorHandler[] = [];

export function onAPIError(handler: ErrorHandler) {
  errorHandlers.push(handler);
  return () => {
    errorHandlers = errorHandlers.filter(h => h !== handler);
  };
}

function emitAPIError(error: APIError) {
  errorHandlers.forEach(handler => handler(error));
}

function extractErrorDetails(error: AxiosError) {
  const data = (error.response?.data as any) || {};
  const code = data?.code || data?.error?.code || 'UNKNOWN_ERROR';
  const message =
    data?.message ||
    data?.error?.message ||
    data?.error ||
    error.message ||
    'An error occurred';
  return { code, message, data };
}

// ============================================================================
// CIRCUIT BREAKER
// ============================================================================

class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private _state: 'closed' | 'open' | 'half-open' = 'closed';

  private readonly threshold = 5;
  private readonly resetTimeout = 30_000; // 30s before attempting recovery

  get state() { return this._state; }

  recordFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();
    if (this.failures >= this.threshold) {
      this._state = 'open';
      if (isDevelopment()) {
        console.warn(`[Circuit Breaker] OPEN after ${this.failures} consecutive failures`);
      }
    }
  }

  recordSuccess(): void {
    if (this._state === 'half-open') {
      if (isDevelopment()) {
        console.log('[Circuit Breaker] CLOSED — recovery successful');
      }
    }
    this.failures = 0;
    this._state = 'closed';
  }

  canRequest(): boolean {
    if (this._state === 'closed') return true;
    if (this._state === 'open') {
      if (Date.now() - this.lastFailureTime > this.resetTimeout) {
        this._state = 'half-open';
        return true; // Allow one probe request
      }
      return false;
    }
    return true; // half-open — allow
  }

  reset(): void {
    this.failures = 0;
    this._state = 'closed';
    this.lastFailureTime = 0;
  }
}

const circuitBreaker = new CircuitBreaker();

/** Exposed for OfflineBanner / status indicators */
export { circuitBreaker };

// ============================================================================
// RETRY CONFIGURATION
// ============================================================================

// Disable axios-level retry in Vitest so that React Query's error state can be
// asserted synchronously. Production/dev keep the full exponential backoff.
const IS_TEST_ENV = import.meta.env.MODE === 'test';

const MAX_RETRIES = IS_TEST_ENV ? 0 : 3;
const RETRY_BASE_DELAY_MS = 1_000; // 1s → 2s → 4s
const RETRY_MAX_DELAY_MS = 8_000;

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;       // 401 token-refresh flag (existing)
  _retryCount?: number;   // 5xx retry counter
  _noRetry?: boolean;     // Opt-out flag for specific requests
}

function isRetryableMethod(_method: string | undefined): boolean {
  // GET, HEAD, OPTIONS are always safe to retry.
  // POST/PUT/PATCH/DELETE only if idempotent (has Idempotency-Key) — but for 5xx
  // the server never processed the request, so retry is safe for all methods.
  return true; // 5xx means server error, safe to retry regardless of method
}

function retryDelay(attempt: number): number {
  const exponential = Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, attempt), RETRY_MAX_DELAY_MS);
  const jitter = Math.random() * 1_000; // 0-1s random jitter
  return exponential + jitter;
}

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

export const TokenManager = {
  getAccessToken(): string | null {
    return localStorage.getItem('adjudon_token');
  },

  setAccessToken(token: string): void {
    // Guard against malformed JWTs leaking into storage. Valid JWT = 3 parts.
    // Tokens with 4+ parts are a known silent-failure mode (e.g., `id.refresh.payload.sig`
    // accidentally concatenated) that causes TOKEN_VERIFICATION_FAILED on every request.
    const parts = typeof token === 'string' ? token.split('.') : [];
    if (parts.length !== 3) {
      const partLens = parts.map((p) => p.length);
      const firstPartPreview = parts[0]?.slice(0, 30) ?? '';
      // eslint-disable-next-line no-console
      console.error(
        '[TokenManager] Refused to store malformed JWT ' + JSON.stringify({
          partsCount: parts.length,
          partLengths: partLens,
          tokenLength: typeof token === 'string' ? token.length : 0,
          tokenType: typeof token,
          firstPartPreview,
          lastPartPreview: parts[parts.length - 1]?.slice(-20) ?? '',
          rawToken: typeof token === 'string' ? token : String(token),
        }) +
        '\nCaller stack:\n' + (new Error().stack ?? '(no stack)')
      );
      return;
    }
    localStorage.setItem('adjudon_token', token);
    scheduleTokenRefresh();
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('adjudon_refresh_token');
  },

  setRefreshToken(token: string): void {
    localStorage.setItem('adjudon_refresh_token', token);
  },

  clearTokens(): void {
    localStorage.removeItem('adjudon_token');
    localStorage.removeItem('adjudon_refresh_token');
    cancelTokenRefresh();
  },

  setTokens(accessToken: string, refreshToken?: string): void {
    this.setAccessToken(accessToken);
    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }
  },
};

// ============================================================================
// CSRF TOKEN MANAGEMENT
// ============================================================================

function getCSRFToken(): string | null {
  // Try to get from meta tag
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }

  // Try to get from cookie
  const cookieMatch = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  if (cookieMatch) {
    return decodeURIComponent(cookieMatch[1]);
  }

  return null;
}

// ============================================================================
// AXIOS INSTANCE
// ============================================================================

export const apiClient: AxiosInstance = axios.create({
  baseURL: ENV.API_BASE_URL,
  timeout: ENV.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for CSRF
});

// ============================================================================
// PROACTIVE TOKEN REFRESH (E-13)
// ============================================================================
// POST /api/auth/refresh issues a new JWT if the current one is still valid.
// On 401 (expired/revoked token) we clear tokens and redirect to login.
// Proactive refresh fires 5 minutes before JWT expiry to keep sessions alive.

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

function decodeTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function cancelTokenRefresh() {
  if (_refreshTimer) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

function scheduleTokenRefresh() {
  cancelTokenRefresh();

  const token = TokenManager.getAccessToken();
  if (!token) return;

  const expiresAt = decodeTokenExpiry(token);
  if (!expiresAt) return;

  // Refresh 5 minutes before expiry
  const delay = expiresAt - 5 * 60 * 1000 - Date.now();
  if (delay <= 0) return; // Already past refresh window

  _refreshTimer = setTimeout(async () => {
    try {
      const response = await apiClient.post('/api/auth/refresh');
      const newToken = response.data?.data?.token || response.data?.token;
      if (newToken) {
        TokenManager.setAccessToken(newToken); // re-triggers scheduleTokenRefresh
      }
    } catch {
      // Refresh failed — token will expire and 401 handler takes over
    }
  }, delay);
}

// Bootstrap: if a token already exists on page load, schedule its refresh
scheduleTokenRefresh();

// ============================================================================
// RATE LIMIT — COUNTDOWN TOAST
// ============================================================================

let _rateLimitToastId: string | number | undefined;

function showRateLimitCountdown(retryAfterSec: number): void {
  let remaining = retryAfterSec;

  // Dismiss previous countdown if active
  if (_rateLimitToastId !== undefined) {
    toast.dismiss(_rateLimitToastId);
  }

  _rateLimitToastId = toast.error(`Rate limited — retry in ${remaining}s`, {
    duration: (retryAfterSec + 1) * 1_000,
    id: 'rate-limit-countdown',
  });

  const interval = setInterval(() => {
    remaining--;
    if (remaining <= 0) {
      clearInterval(interval);
      toast.dismiss('rate-limit-countdown');
      toast.success('Rate limit lifted — you can retry now', { duration: 3000 });
      _rateLimitToastId = undefined;
      return;
    }
    toast.error(`Rate limited — retry in ${remaining}s`, {
      id: 'rate-limit-countdown',
      duration: (remaining + 1) * 1_000,
    });
  }, 1_000);
}

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Circuit breaker check
    if (!circuitBreaker.canRequest()) {
      const error = new APIError(
        503,
        'CIRCUIT_OPEN',
        'Service temporarily unavailable. Retrying shortly...',
        undefined,
        'system'
      );
      return Promise.reject(error);
    }

    // Add access token
    const token = TokenManager.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token
    const csrfToken = getCSRFToken();
    if (csrfToken && config.headers) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Add active workspace ID so backend filters data to the correct workspace
    const workspaceId = localStorage.getItem('adjudon_active_workspace');
    if (workspaceId && config.headers) {
      config.headers['X-Workspace-ID'] = workspaceId;
    }

    // Add active regulated entity (Aufsichtsobjekt) so the backend scopes
    // per-entity compliance surfaces (attestations etc.). Absent → backend uses
    // the org's default entity / consolidated view.
    const entityId = localStorage.getItem('adjudon_active_entity');
    if (entityId && config.headers) {
      config.headers['X-Entity-ID'] = entityId;
    }

    // Log request in development
    if (isDevelopment()) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    // Add to pending requests
    addPendingRequest(config);

    return config;
  },
  (error) => {
    if (isDevelopment()) {
      console.error('[API] Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (isDevelopment()) {
      console.log(`[API] ${response.status} ${response.config.url}`, response.data);
    }

    // Remove from pending requests
    removePendingRequest(response.config);

    // Success = circuit breaker recovery
    circuitBreaker.recordSuccess();

    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;

    if (isDevelopment()) {
      console.error('[API] Response Error:', {
        url: config?.url,
        status: error.response?.status,
        data: error.response?.data,
        retryCount: config?._retryCount ?? 0,
      });
    }

    // Remove from pending requests
    if (config) {
      removePendingRequest(config);
    }

    const status = error.response?.status;
    const isNetworkError = !error.response && error.message === 'Network Error';

    // ── 401 — Unauthorized ──────────────────────────────────────────
    if (status === 401 && config && !config._retry) {
      config._retry = true;

      // Enrichment endpoints must NEVER trigger a logout-cascade. If SSE token
      // exchange 401s, the hook retries silently — the user stays on the page.
      const url = config.url ?? '';
      const isEnrichmentEndpoint = url.includes('/api/auth/stream-token');

      // DEV-only: decode the current JWT so we can see expiry / signature / org mismatch
      // without needing backend terminal access.
      const diagnose401 = () => {
        if (!import.meta.env.DEV) return;
        const token = TokenManager.getAccessToken();
        let jwtDiag: Record<string, unknown> = { hasToken: Boolean(token) };
        if (token) {
          const parts = token.split('.');
          if (parts.length !== 3) {
            jwtDiag = { ...jwtDiag, malformed: true, partsCount: parts.length };
          } else {
            try {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              const nowSec = Math.floor(Date.now() / 1000);
              const exp = typeof payload.exp === 'number' ? payload.exp : null;
              const iat = typeof payload.iat === 'number' ? payload.iat : null;
              jwtDiag = {
                ...jwtDiag,
                userId: payload.id ?? payload.sub ?? null,
                organizationId: payload.organizationId ?? null,
                issuedAt: iat ? new Date(iat * 1000).toISOString() : null,
                expiresAt: exp ? new Date(exp * 1000).toISOString() : null,
                secondsUntilExpiry: exp ? exp - nowSec : null,
                isExpired: exp ? exp < nowSec : null,
                ageSeconds: iat ? nowSec - iat : null,
              };
            } catch (e) {
              jwtDiag = { ...jwtDiag, decodeError: (e as Error).message };
            }
          }
        }
        // eslint-disable-next-line no-console
        console.warn(
          '[apiClient] 401 diag ' + JSON.stringify({
            endpoint: `${config.method?.toUpperCase()} ${url}`,
            backendCode: (error.response?.data as any)?.code,
            backendMessage: (error.response?.data as any)?.message || (error.response?.data as any)?.error,
            jwt: jwtDiag,
          }, null, 2)
        );
      };

      if (isEnrichmentEndpoint) {
        diagnose401();
        return Promise.reject(error);
      }

      diagnose401();
      TokenManager.clearTokens();
      if (typeof window !== 'undefined') {
        // Skip the hard redirect if the user is already on an unauth route.
        // Without this guard, a 401 fired from /login (e.g. by a globally-
        // mounted component querying an auth-protected endpoint) reloads
        // the page, which re-mounts the component, which re-fires the
        // request — an infinite refresh loop visible in backend logs as
        // sub-ms `GET /api/...  401` repetition.
        const path = window.location.pathname;
        const onUnauthRoute =
          path.startsWith('/login') ||
          path.startsWith('/auth') ||
          path.startsWith('/signup') ||
          path.startsWith('/accept-invite') ||
          path.startsWith('/forgot-password') ||
          path.startsWith('/reset-password') ||
          path.startsWith('/verify-email') ||
          path.startsWith('/magic-link');
        if (!onUnauthRoute) {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }

    // ── 503 — Maintenance Mode ──────────────────────────────────────
    if (status === 503 && (error.response?.data as any)?.code === 'MAINTENANCE_MODE') {
      window.dispatchEvent(new CustomEvent('adjudon:maintenance', { detail: { on: true } }));
      return Promise.reject(error);
    }

    // ── 5xx — Exponential Backoff Retry ─────────────────────────────
    if (status && status >= 500 && config && !config._noRetry) {
      const retryCount = config._retryCount ?? 0;

      if (retryCount < MAX_RETRIES && isRetryableMethod(config.method)) {
        config._retryCount = retryCount + 1;
        const delay = retryDelay(retryCount);

        if (isDevelopment()) {
          console.log(`[API] Retry ${config._retryCount}/${MAX_RETRIES} in ${Math.round(delay)}ms — ${config.url}`);
        }

        await new Promise(resolve => setTimeout(resolve, delay));
        return apiClient(config);
      }

      // All retries exhausted — record failure for circuit breaker
      circuitBreaker.recordFailure();

      const details = extractErrorDetails(error);
      const apiError = new APIError(
        status,
        details.code,
        circuitBreaker.state === 'open'
          ? 'Service temporarily unavailable. We are working on it.'
          : 'Something went wrong on our end. Please try again.',
        details.data,
        'system'
      );
      emitAPIError(apiError);

      if (circuitBreaker.state === 'open') {
        toast.error('Service temporarily unavailable', {
          description: 'Automatic recovery in progress...',
          id: 'circuit-open',
        });
      } else {
        toast.error('Server error — please try again');
      }

      return Promise.reject(apiError);
    }

    // ── 429 — Rate Limit with Countdown ─────────────────────────────
    if (status === 429) {
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const retryAfterSec = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 60;
      const retryAfter = Number.isFinite(retryAfterSec) && retryAfterSec > 0 ? retryAfterSec : 60;

      const apiError = new APIError(
        429,
        'RATE_LIMIT_EXCEEDED',
        `Rate limited. Retry in ${retryAfter} seconds.`,
        { ...((error.response?.data as any) || {}), retryAfter },
        'rate_limit'
      );

      emitAPIError(apiError);
      showRateLimitCountdown(retryAfter);

      return Promise.reject(apiError);
    }

    // ── 403 — Forbidden (Usage limit / Plan-gated feature) ──────────
    if (status === 403) {
      const errorData = error.response?.data as any;

      if (errorData?.code === 'USAGE_LIMIT_EXCEEDED') {
        const apiError = new APIError(
          403,
          'USAGE_LIMIT_EXCEEDED',
          'You have reached your plan limit. Please upgrade to continue.',
          errorData,
          'auth'
        );
        emitAPIError(apiError);
        return Promise.reject(apiError);
      }

      // requireFeature() (middleware/requirePlan.js) returns 403 +
      // 'UPGRADE_REQUIRED' when the org's plan doesn't include the
      // feature gate (e.g. federatedLearning is custom-tier only).
      // Only the global UpgradeModal-pop should fire for MUTATIONS
      // (user actively tried something that needs upgrade — e.g.
      // "Kick off federated round"). Plain page-load GETs are handled
      // by the page's own <PlanLock> render, so emitting here would
      // pop the modal ON TOP of that PlanLock — a doppel-lock the
      // user has to dismiss twice. (Federated Learning bug filed
      // 2026-05-11.) The error itself still propagates through React
      // Query's rejection path so the page can branch on err.code.
      if (errorData?.code === 'UPGRADE_REQUIRED') {
        const apiError = new APIError(
          403,
          'UPGRADE_REQUIRED',
          'This feature is not available on your current plan.',
          errorData,
          'auth'
        );
        const method = (config?.method ?? 'get').toLowerCase();
        if (method !== 'get') {
          emitAPIError(apiError);
        }
        return Promise.reject(apiError);
      }
    }

    // ── Network Error ───────────────────────────────────────────────
    if (isNetworkError) {
      const apiError = new APIError(
        0,
        'NETWORK_ERROR',
        'No internet connection. Changes will sync when you reconnect.',
        undefined,
        'network'
      );
      toast.error('Connection lost', {
        description: 'Actions will be saved and synced when you reconnect.',
        id: 'network-error',
      });
      return Promise.reject(apiError);
    }

    // ── All other errors → categorized APIError ─────────────────────
    const details = extractErrorDetails(error);
    const category = categorizeError(status, false);
    const apiError = new APIError(
      status || 500,
      details.code,
      details.message,
      details.data,
      category
    );

    return Promise.reject(apiError);
  }
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Handle file upload with progress tracking
 */
export async function uploadFile(
  endpoint: string,
  file: File,
  fieldName: string = 'file',
  onProgress?: (progress: number) => void
) {
  const formData = new FormData();
  formData.append(fieldName, file);

  return apiClient.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
}

/**
 * Download file from API
 */
export interface DownloadFileMeta {
  /** Server-reported total — usually from `X-Total-Count` header. */
  totalCount?: number;
  /** True if the server flagged the export as truncated (X-Total-Truncated). */
  truncated?: boolean;
}

export async function downloadFile(
  endpoint: string,
  filename: string,
  options?: { params?: Record<string, string | number | boolean | undefined> },
): Promise<DownloadFileMeta> {
  const response = await apiClient.get(endpoint, {
    responseType: 'blob',
    params: options?.params,
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  // Surface server-side cap signals so the caller can warn the user
  // when the download was truncated. Audit log export caps at 10k CSV
  // / 2k PDF — without this the user thinks they downloaded everything.
  const headers = response.headers;
  const totalRaw =
    typeof headers?.get === 'function'
      ? headers.get('x-total-count')
      : (headers as Record<string, string> | undefined)?.['x-total-count'];
  const truncRaw =
    typeof headers?.get === 'function'
      ? headers.get('x-total-truncated')
      : (headers as Record<string, string> | undefined)?.['x-total-truncated'];
  return {
    totalCount: totalRaw ? Number(totalRaw) : undefined,
    truncated: truncRaw === '1' || truncRaw === 'true',
  };
}

export default apiClient;
